/**
 * A shuffle that gives the same order every time for the same seed
 * (`LN-WEB02`).
 *
 * The checkpoint's options were shown in file order, and in the C track the
 * correct option is first in 296 of 300 checkpoint questions, so "always pick
 * the first" passed every checkpoint. The order is now drawn from a seed, the
 * lesson id and the question id, for three reasons:
 *
 * - **No `Math.random`.** The server and the browser compute the same order
 *   from the same props, so nothing can disagree at hydration.
 * - **A reload does not reshuffle.** A reader who comes back to a question
 *   finds the option they were looking at where they left it.
 * - **Not per learner.** The user id is not known on the server, and reading it
 *   later would move the options under a reader who is already looking at
 *   them. The checkpoint is not scored (`LearnCheckpoint` says why), so a
 *   shared order costs nothing a per-learner one would protect.
 *
 * FNV-1a turns the seed into 32 bits and mulberry32 draws from them; both are
 * small, fast and fully specified, which is all a display order needs. This is
 * not a security primitive and must never be used as one.
 */
function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A new array in a Fisher-Yates order drawn from `seed`; the input is untouched. */
export function seededShuffle<T>(items: readonly T[], seed: string): T[] {
  const out = [...items];
  const random = mulberry32(hashSeed(seed));
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    const held = out[i];
    out[i] = out[j];
    out[j] = held;
  }
  return out;
}
